import { useState, useEffect } from 'react';
import { Share2, Copy, Check, X, MessageCircle, Mail, Gift } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getReferralCode, getReferralCodeSync, buildShareUrlSync } from '../utils/shareWithReferral';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareButton = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  // Seed from localStorage synchronously so the FIRST render already has the ref code
  // when the user is logged in. Avoids a race where clicking "Share" instantly would
  // share the plain origin without ?ref=.
  const [referralCode, setReferralCode] = useState(() => getReferralCodeSync());

  useEffect(() => {
    // Refresh from server in the background (updates cache for next time).
    getReferralCode().then(code => {
      if (code) setReferralCode(code);
    });
  }, []);

  // QR / share now targets the user's OWN BARE MC profile so a friend can
  // scan → land directly on the profile. The referral code is appended so
  // the sharer still earns points when a new rider signs up. If the user has
  // no profile yet (logged out), we fall back to the invite link.
  const buildProfileUrl = (code) => {
    if (user?.id) {
      const base = `${window.location.origin}/profil/${user.id}`;
      return code ? `${base}?ref=${code}` : base;
    }
    return code ? `${window.location.origin}/register?ref=${code}` : window.location.origin;
  };

  const shareUrl = buildProfileUrl(referralCode);

  const shareText = user?.id
    ? 'Se min profil på BareMC 🏍️'
    : (referralCode ? 'Kom med paa BareMC og saml point! Opret din profil her:' : t('landing.shareText'));

  const shareData = {
    title: 'BareMC',
    text: shareText,
    url: shareUrl,
  };

  // Always resolve the latest referral code at click time.
  const resolveShareData = async () => {
    const code = await getReferralCode();
    if (code && code !== referralCode) setReferralCode(code);
    const url = buildProfileUrl(code);
    const text = user?.id
      ? 'Se min profil på BareMC 🏍️'
      : (code ? 'Kom med paa BareMC og saml point! Opret din profil her:' : t('landing.shareText'));
    return { title: 'BareMC', text, url };
  };

  const handleNativeShare = async () => {
    const data = await resolveShareData();
    // Keep the visible banner in sync if we just resolved a code.
    if (data.url.includes('?ref=')) {
      const c = new URL(data.url).searchParams.get('ref');
      if (c && c !== referralCode) setReferralCode(c);
    }
    if (navigator.share) {
      try {
        await navigator.share(data);
        toast.success(t('share.shared'));
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    } else {
      setShowModal(true);
    }
  };

  const handleCopyLink = async () => {
    const data = await resolveShareData();
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      toast.success(t('common.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = data.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success(t('common.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLinks = [
    {
      name: 'SMS',
      icon: MessageCircle,
      href: `sms:?body=${encodeURIComponent(shareData.text + ' ' + shareData.url)}`,
      color: 'bg-green-600'
    },
    {
      name: 'E-mail',
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${encodeURIComponent(shareData.text + '\n\n' + shareData.url)}`,
      color: 'bg-zinc-600'
    },
    {
      name: 'WhatsApp',
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      href: `https://wa.me/?text=${encodeURIComponent(shareData.text + ' ' + shareData.url)}`,
      color: 'bg-green-500'
    },
    {
      name: 'Messenger',
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
        </svg>
      ),
      href: `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareData.url)}&app_id=&redirect_uri=${encodeURIComponent(shareData.url)}`,
      color: 'bg-gradient-to-r from-purple-500 to-pink-500'
    },
    {
      name: 'Facebook',
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`,
      color: 'bg-blue-600'
    }
  ];

  return (
    <>
      {/* ── Professional "Del din BARE MC-profil" card ── */}
      <div
        className="w-full max-w-sm mx-auto rounded-3xl overflow-hidden"
        style={{
          background: 'rgba(16,22,16,0.92)',
          border: '1px solid rgba(45,255,136,0.18)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
        data-testid="profile-qr-card"
      >
        {/* Heading */}
        <div className="px-6 pt-7 pb-1 text-center">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase" style={{ color: '#2DFF88' }}>
            Del din BARE MC-profil
          </p>
          <p className="text-white/60 text-[13px] leading-snug mt-2 max-w-[260px] mx-auto">
            Lad en anden rytter scanne din kode og lande direkte på din profil.
          </p>
        </div>

        {/* Big QR — the visual focus */}
        <div className="flex justify-center px-6 pt-5 pb-5">
          <div
            className="rounded-3xl p-5"
            style={{ background: '#fff', boxShadow: '0 12px 40px rgba(45,255,136,0.14)' }}
            data-testid="qr-code-container"
          >
            <QRCodeSVG value={shareUrl} size={212} level="M" marginSize={1} style={{ display: 'block' }} />
          </div>
        </div>

        {/* Primary CTA */}
        <div className="px-6">
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-bold text-[15px] active:scale-[0.98] transition-transform"
            style={{ background: '#2DFF88', color: '#0a1410' }}
            data-testid="share-button"
          >
            <Share2 className="w-5 h-5" />
            <span>Del QR-kode</span>
          </button>
        </div>

        {/* Secondary: copy link */}
        <div className="px-6 pt-2.5 pb-6">
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl font-semibold text-[13px] border transition active:scale-[0.98]"
            style={{ borderColor: 'rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.8)' }}
            data-testid="copy-link-button"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400">Kopieret!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>{t('gen.copyLink')}</span>
              </>
            )}
          </button>
          {referralCode && (
            <div className="mt-3 flex items-center justify-center gap-2" data-testid="share-points-info">
              <Gift className="w-3.5 h-3.5" style={{ color: '#2DFF88' }} />
              <span className="text-[11px] text-white/50">+50 point når en ny ven opretter sig</span>
            </div>
          )}
        </div>
      </div>

      {/* Fallback Modal for browsers without Web Share API */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white/5 rounded-xl-silver rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl text-cream">{t('landing.shareWithFriends')}</h3>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-cream-muted" />
              </button>
            </div>

            {/* Copy Link */}
            <div className="mb-6">
              <div className="flex items-center gap-2 p-3 bg-zinc-900/80 rounded-xl border border-white/10">
                <input 
                  type="text" 
                  value={shareData.url}
                  readOnly
                  className="flex-1 bg-transparent text-cream-muted text-sm truncate outline-none"
                />
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-400 text-zinc-900 font-semibold text-xs px-3 py-1.5 rounded-lg hover:bg-emerald-300 hover:text-zinc-900 transition-colors whitespace-nowrap"
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('common.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      {t('common.copy')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Share Options */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {shareLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-white/10 transition-all touch-manipulation"
                  data-testid={`share-${link.name.toLowerCase()}`}
                >
                  <div className={`w-12 h-12 rounded-full ${link.color} flex items-center justify-center text-white`}>
                    {typeof link.icon === 'function' ? <link.icon /> : <link.icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs text-cream-muted">{link.name}</span>
                </a>
              ))}
            </div>

            {/* Install Prompt */}
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-cream-muted text-sm">
                💡 Tip: Installer BareMC på din hjemmeskærm for hurtig adgang!
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;
